import React, { useMemo, useState } from 'react';
import { useData } from "../backend/firebase";
import SEO from '../components/SEO';
import { getImagePath } from '../utils/imageRegistry';
import { DateTime } from 'luxon';
import { StreamEmbed } from '../components/StreamEmbed'; 
import MultiStreamPlayer from '../components/MultiStreamPlayer';
import HandAnalyzer from '../components/HandAnalyzer';
import { useWindowSize } from '../hooks/useWindowSize';

const Standings = () => {
    const { schedule, teams, brackets, userRole, allLineups } = useData();
    // --- NAVIGATION STATE ---
    const [mainTab, setMainTab] = useState('RR'); // 'RR' or 'KO'
    const [rrTab, setRrTab] = useState('A');      // 'A' or 'B'
    const [koTab, setKoTab] = useState('main');   // 'main' or 'consolation'
    const [sortMode, setSortMode] = useState('vp'); // 'vp' or 'extra'
    const [extraColumnMode, setExtraColumnMode] = useState('totalImps');
    const [extraColIdx, setExtraColIdx] = useState(0);
    const extraColLabels = ['Total VPs', 'IMPs Swung', 'IMPs / Match', 'IMPs / Board'];
    
    // --- ADMIN STATE ---
    const [detailedTeamId, setDetailedTeamId] = useState("");
    const [detailedMatchId, setDetailedMatchId] = useState("");
    const [focusTeamId, setFocusTeamId] = useState(null);
    const [activeBoardPlay, setActiveBoardPlay] = useState(null);
    const [activeTeamVodId, setActiveTeamVodId] = useState(null);

    // --- GLOBAL TOOLTIP STATE ---
    const [globalTooltip, setGlobalTooltip] = useState({ visible: false, x: 0, y: 0, text: '', id: null });
    const isScrolling = React.useRef(false);
    const scrollTimeout = React.useRef(null);
    const { isMobile } = useWindowSize();
    const isAdmin = userRole?.role === 'admin';

    React.useEffect(() => {
        const hide = () => setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null });
        
        const handleScroll = () => {
            isScrolling.current = true; // Engage the lock
            hide(); // Nuke the tooltip
            
            // Release the lock 250ms after the scrolling completely stops
            clearTimeout(scrollTimeout.current);
            scrollTimeout.current = setTimeout(() => {
                isScrolling.current = false;
            }, isMobile ? 200 : 0 );
        };

        window.addEventListener('click', hide);
        window.addEventListener('scroll', handleScroll, true); 
        return () => {
            window.removeEventListener('click', hide);
            window.removeEventListener('scroll', handleScroll, true);
            clearTimeout(scrollTimeout.current);
        };
    }, []);


    // 2. Guarantee the tooltip vanishes if a view unmounts underneath it
    React.useEffect(() => {
        setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null });
    }, [mainTab, rrTab, koTab, detailedTeamId, detailedMatchId]);


    const detailedMatchRecord = useMemo(() => {
        if (!detailedMatchId) return null;
        return schedule.find(m => m.id === detailedMatchId);
    }, [detailedMatchId, schedule]);



    const WIN_COLOR = '#6cbd8c'; // Softer green
    const LOSE_COLOR = '#ed7666'; // Softer red
    const TIE_COLOR = '#6B7280';  // Grey for zero

    const formatContract = (contractStr) => {
        // Strict 80px bounding box, centered in the cell
        const containerStyle = {
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            width: '60px', 
            margin: '0 auto', 
            fontWeight: 'bold'
        };

        if (!contractStr || contractStr.trim() === '' || contractStr.toUpperCase() === 'P' || contractStr.toUpperCase() === 'PASS') {
            return (
                <div style={containerStyle}>
                    <span style={{color: '#8F9EB5'}}>Pass</span>
                    <span></span> {/* Empty span to maintain flex spacing */}
                </div>
            );
        }
        
        // Split into: [1] Level, [2] Suit, [3] Everything Else (Doubles, Declarer, Results)
        const match = contractStr.trim().match(/^([1-7])([CDHSN])(.*)$/i);
        if (!match) return (
            <div style={containerStyle}>
                <span>{contractStr}</span>
                <span></span>
            </div>
        );
        
        const level = match[1];
        const suit = match[2].toUpperCase();
        const rest = match[3];
        
        const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
        const COLORS = { 'S': '#c0cAdF', 'H': '#FF3366', 'D': '#FF3366', 'C': '#c0cAdF', 'N': '#c0cAdF' };
        
        let modifiers = rest;
        let resultSpan = <span></span>; // Default empty span for spacing

        // Check if the 'rest' string ends with a result (=, +x, -x)
        const resultMatch = rest.match(/^(.*?)([-=+](?:\d+)?)$/);
        if (resultMatch) {
            modifiers = resultMatch[1]; 
            const resultText = resultMatch[2]; 
            
            const isWin = resultText.startsWith('=') || resultText.startsWith('+');
            const resultColor = isWin ? WIN_COLOR : LOSE_COLOR;
            
            resultSpan = <span style={{color: resultColor}}>{resultText}</span>;
        }
        
        return (
            <div style={containerStyle}>
                <span style={{color:'#c0cAdF'}}>
                    {level}<span style={{color: COLORS[suit]}}>{SYMBOLS[suit]}</span>{modifiers}
                </span>
                {resultSpan}
            </div>
        );
    };

    const PlayerBadge = ({ name, color }) => (
        <div style={{ 
            background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}`, color: '#fff', 
            padding: '4px 6px', borderRadius: 6, fontSize: 9, textAlign: 'center', fontWeight: 'bold',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            boxShadow: `0 2px 8px rgba(0,0,0,0.2)`, width: 70
        }}>
            {name || '-'}
        </div>
    );

    const MiniRosterGrid = ({ tNS, tEW, cNS, cEW, title }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'rgba(0,0,0,0.0)', padding: '6px', borderRadius: 8, border: '1px solid rgba(255, 255, 255, 0.00)', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'center', marginBottom: 6, fontSize: 10, color: '#A0AABF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>{title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <PlayerBadge name={tNS?.north} color={cNS} />
                <div style={{ display: 'flex', gap: 2 }}>
                    <PlayerBadge name={tEW?.west} color={cEW} />
                    <PlayerBadge name={tEW?.east} color={cEW} />
                </div>
                <PlayerBadge name={tNS?.south} color={cNS} />
            </div>
        </div>
    );

    // Update these hexes to perfectly match your Prize Pool graphic!
    const topRankColors = ['#FFD700', '#E0E0E0', '#CD7F32', '#B088FF']; // Gold, Silver, Bronze, Purple
    const topRankBgs = ['rgba(255, 215, 0, 0.08)', 'rgba(192, 192, 192, 0.08)', 'rgba(205, 127, 50, 0.08)', 'rgba(155, 89, 182, 0.08)'];


    const getEffectiveStartTime = (isoString) => {
        const time = DateTime.fromISO(isoString, { zone: 'America/Los_Angeles' }).toLocal();
        return userRole ? time : time.plus({ minutes: 15 });
    };

    // --- 1. CORE DATA ENGINE ---
    const processedStandings = useMemo(() => {
        const groups = {}; 
        const rrMatches = schedule.filter(m => m.stage === 'RR' || !m.stage);

        rrMatches.forEach(m => {
            const t1 = m.team1?.toString();
            const t2 = m.team2?.toString();
            if (!t1 || !t2) return;

            const t1Data = teams.find(t => t.id.toString() === t1);
            const g = m.group || t1Data?.rrGroup || "Unassigned";

            if (!groups[g]) groups[g] = {};
            
            [t1, t2].forEach(tId => {
                if (!groups[g][tId]) {
                    const teamData = teams.find(t => t.id.toString() === tId);
                    groups[g][tId] = {
                        id: tId,
                        team: teamData,
                        played: 0,
                        totalVp: 0,
                        avgVp: 0,
                        impsWon: 0,
                        impsLost: 0,
                        totalBoards: 0,
                        impMargin: 0,
                        timeline: [] 
                    };
                }
            });
        });

        rrMatches.forEach(m => {
            const t1 = m.team1?.toString();
            const t2 = m.team2?.toString();
            if (!t1 || !t2) return;

            const t1Data = teams.find(t => t.id.toString() === t1);
            const g = m.group || t1Data?.rrGroup || "Unassigned";

            groups[g][t1].timeline.push({ ...m, isTeam1: true });
            groups[g][t2].timeline.push({ ...m, isTeam1: false });

            if (m.isFinal || (isAdmin && (parseFloat(m.team1_vp) + parseFloat(m.team2_vp) > 1))) {
                const vp1 = parseFloat(m.team1_vp) || 0;
                const vp2 = parseFloat(m.team2_vp) || 0;
                const imp1 = parseFloat(m.team1_score) || 0;
                const imp2 = parseFloat(m.team2_score) || 0;

                const bCount = m.parsedBoards?.length || m.boards || m.boardCount || 16;

                groups[g][t1].played += 1;
                groups[g][t1].totalVp += vp1;
                groups[g][t1].impsWon += imp1;
                groups[g][t1].impsLost += imp2;
                groups[g][t1].totalBoards += bCount;

                groups[g][t2].played += 1;
                groups[g][t2].totalVp += vp2;
                groups[g][t2].impsWon += imp2;
                groups[g][t2].impsLost += imp1;
                groups[g][t2].totalBoards += bCount;
            }
        });

        const sortedGroups = {};
        Object.keys(groups).forEach(g => {
            const teamArray = Object.values(groups[g]);
            
            teamArray.forEach(t => {
                t.avgVp = t.played > 0 ? (t.totalVp / t.played) : 0;
                t.impMargin = t.impsWon - t.impsLost;

                t.totalImpsSwung = t.impsWon + t.impsLost; 
                t.impsPerMatch = t.played > 0 ? t.totalImpsSwung / t.played : 0; 
                t.impsPerBoard = t.totalBoards > 0 ? t.totalImpsSwung / t.totalBoards : 0;

                t.timeline.sort((a, b) => DateTime.fromISO(a.startTime) - DateTime.fromISO(b.startTime));
            });

            const getH2H = (idA, idB) => {
                const match = rrMatches.find(m => (m.isFinal || (isAdmin && (parseFloat(m.team1_vp) + parseFloat(m.team2_vp) > 1))) && ((m.team1?.toString() === idA && m.team2?.toString() === idB) || (m.team1?.toString() === idB && m.team2?.toString() === idA)));
                if (!match) return 0;
                const vpA = match.team1?.toString() === idA ? parseFloat(match.team1_vp) : parseFloat(match.team2_vp);
                const vpB = match.team1?.toString() === idB ? parseFloat(match.team1_vp) : parseFloat(match.team2_vp);
                return vpA - vpB;
            };

            teamArray.sort((a, b) => {
                if (sortMode === 'extra') {
                    let valA, valB;
                    if (extraColumnMode === 'totalImps') { valA = a.totalImpsSwung; valB = b.totalImpsSwung; }
                    else if (extraColumnMode === 'impsPerMatch') { valA = a.impsPerMatch; valB = b.impsPerMatch; }
                    else if (extraColumnMode === 'impsPerBoard') { valA = a.impsPerBoard; valB = b.impsPerBoard; }

                    if (Math.abs(valB - valA) > 0.001) return valB - valA;
                } else {
                    if (Math.abs(b.avgVp - a.avgVp) > 0.001) return b.avgVp - a.avgVp;
                }

                if (Math.abs(b.avgVp - a.avgVp) > 0.001) return b.avgVp - a.avgVp;
                const h2h = getH2H(a.id, b.id);
                if (Math.abs(h2h) > 0.001) return h2h;
                return b.impMargin - a.impMargin;
            });

            sortedGroups[g] = teamArray;
        });

        return sortedGroups;
    }, [schedule, teams, sortMode, extraColumnMode, isAdmin]);

    // --- 1.5 AUTO-POPULATE HYPOTHETICAL BRACKETS ---
    const computedBrackets = useMemo(() => {
        // Deep copy the manual brackets from Firebase so we don't mutate state
        const calc = JSON.parse(JSON.stringify(brackets || { main: {}, consolation: {} }));
        if (!calc.main) calc.main = {};
        if (!calc.consolation) calc.consolation = {};

        // Helper: Gets a team ID based on Group and Rank (1-indexed)
        const getSeed = (group, rank) => {
            const arr = processedStandings[group];
            return arr && arr.length >= rank ? arr[rank - 1].id : null;
        };

        // Helper: Evaluates a match and returns the Winner or Loser
        const getResultTeam = (match, type) => {
            if (!match || !match.winner) return null;
            if (type === 'winner') return match.winner === 'team1' ? match.team1 : match.team2;
            if (type === 'loser') return match.winner === 'team1' ? match.team2 : match.team1;
            return null;
        };

        // Helper: Aggressively overwrites Firebase data if it's empty or a placeholder like "TBD"
        const setMatch = (obj, matchId, t1_auto, t2_auto) => {
            if (!obj[matchId]) obj[matchId] = {};
            
            const isPlaceholder = (val) => {
                if (!val) return true;
                const s = String(val).trim().toLowerCase();
                // NEW: Added s.includes("select") to catch "select team" or "select"
                return s === "" || s === "none" || s === "tba" || s === "tbd" || s.includes("select");
            };

            if (isPlaceholder(obj[matchId].team1)) obj[matchId].team1 = t1_auto;
            if (isPlaceholder(obj[matchId].team2)) obj[matchId].team2 = t2_auto;
        };

        const m = calc.main;
        const c = calc.consolation;

        // ====================================================================
        // 🏆 1. CHAMPIONSHIP BRACKET PATTERN
        // ====================================================================
        // Claw-ins (Keys start with 'm')
        setMatch(m, 'mpi2', getSeed('A', 4), getSeed('B', 5));
        setMatch(m, 'mpi1', getSeed('A', 5), getSeed('B', 4));

        // Quarterfinals
        setMatch(m, 'mqf1', getSeed('A', 1), getResultTeam(m['mpi1'], 'winner'));
        setMatch(m, 'mqf2', getSeed('A', 3), getSeed('B', 2));
        setMatch(m, 'mqf3', getSeed('B', 3), getSeed('A', 2));
        setMatch(m, 'mqf4', getResultTeam(m['mpi2'], 'winner'), getSeed('B', 1));

        // Semifinals
        setMatch(m, 'msf1', getResultTeam(m['mqf1'], 'winner'), getResultTeam(m['mqf2'], 'winner'));
        setMatch(m, 'msf2', getResultTeam(m['mqf3'], 'winner'), getResultTeam(m['mqf4'], 'winner'));

        // Finals & Bronze
        setMatch(m, 'mfinal', getResultTeam(m['msf1'], 'winner'), getResultTeam(m['msf2'], 'winner'));
        setMatch(m, 'mbronze', getResultTeam(m['msf1'], 'loser'), getResultTeam(m['msf2'], 'loser'));


        // ====================================================================
        // 🛡️ 2. CHALLENGERS BRACKET PATTERN (Consolation)
        // ====================================================================
        // Play-ins
        setMatch(c, 'cpi1', getSeed('A', 8), getSeed('B', 9));
        setMatch(c, 'cpi2', getSeed('A', 9), getSeed('B', 8));

        // Quarterfinals (Perfectly mirroring the Championship cross-seeding)
        // A1 -> mpi2 loser, B1 -> mpi1 loser. A2 -> A6, B2 -> B6, A3 -> A7, B3 -> B7.
        setMatch(c, 'cqf1', getResultTeam(m['mpi1'], 'loser'), getResultTeam(c['cpi1'], 'winner'));
        setMatch(c, 'cqf2', getSeed('A', 7), getSeed('B', 6));
        setMatch(c, 'cqf3', getSeed('B', 7), getSeed('A', 6));
        setMatch(c, 'cqf4', getResultTeam(c['cpi2'], 'winner'), getResultTeam(m['mpi2'], 'loser'));

        // Semifinals
        setMatch(c, 'csf1', getResultTeam(c['cqf1'], 'winner'), getResultTeam(c['cqf2'], 'winner'));
        setMatch(c, 'csf2', getResultTeam(c['cqf3'], 'winner'), getResultTeam(c['cqf4'], 'winner'));

        // Finals & Bronze
        setMatch(c, 'cfinal', getResultTeam(c['csf1'], 'winner'), getResultTeam(c['csf2'], 'winner'));
        setMatch(c, 'cbronze', getResultTeam(c['csf1'], 'loser'), getResultTeam(c['csf2'], 'loser'));

        return calc;
    }, [brackets, processedStandings]);

    const detailedTeamData = useMemo(() => {
        if (!detailedTeamId) return null;
        for (const group of Object.values(processedStandings)) {
            const found = group.find(t => t.id === detailedTeamId);
            if (found) return found;
        }
        return null;
    }, [detailedTeamId, processedStandings]);

    // --- 2. UI HELPERS & COMPONENTS ---
    const TeamIcon = ({ team, size = 32, clickable = false, opacity=1}) => {
        if (!team) return <div style={{width: size, height: size, background: '#333', borderRadius: '50%'}} />;
        const src = team.logoUrl ? getImagePath(team.logoUrl) : (team.logo ? `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}` : null);
        
        const inner = (
            <div style={{ 
                width: size, height: size, borderRadius: '50%', background: '#111', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                flexShrink: 0, padding: 1, boxSizing: 'border-box', border: `2px solid ${team?.color ? (opacity !== undefined ? `color-mix(in srgb, ${team.color} ${opacity * 100}%, transparent)` : team.color) : '#555'}`,
                // flexShrink: 0, padding: 1, boxSizing: 'border-box', border: `2px solid ${team?.color ? (opacity !== undefined ? `${team.color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` : team.color) : '#555'}`,
                // flexShrink: 0, padding: 1, boxSizing: 'border-box', border: `2px solid ${team?.color || '#555'}`,
                transition: 'all 0.2s ease'
            }}>
                {src ? <img src={src} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} /> : <div style={{background: team.color || '#333', width: '100%', height: '100%', borderRadius: '50%'}}></div>}
            </div>
        );

        if (clickable && team.socialLink) {
            return (
                <a 
                    href={team.socialLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ textDecoration: 'none', display: 'flex' }}
                    onClick={e => e.stopPropagation()} // Prevents accidental bubbling
                    onMouseEnter={e => { e.currentTarget.firstChild.style.transform = 'scale(1.15)'; e.currentTarget.firstChild.style.boxShadow = `0 0 15px ${team.color}80`; }}
                    onMouseLeave={e => { e.currentTarget.firstChild.style.transform = 'scale(1)'; e.currentTarget.firstChild.style.boxShadow = 'none'; }}
                >
                    {inner}
                </a>
            );
        }

        return (
            <div style={{ width: size, height: size, borderRadius: '50%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 1, boxSizing: 'border-box', border: `2px solid ${team?.color ? (opacity !== undefined ? `color-mix(in srgb, ${team.color} ${opacity * 100}%, transparent)` : team.color) : '#555'}` }}>
                {src ? <img src={src} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} /> : <div style={{background: team.color || '#333', width: '100%', height: '100%', borderRadius: '50%'}}></div>}
            </div>
        );
    };

    const TimelineItem = ({ matchRecord, focusId }) => {
        const oppId = matchRecord.isTeam1 ? matchRecord.team2 : matchRecord.team1;
        const oppTeam = teams.find(t => t.id.toString() === oppId?.toString());
        const oppColor = oppTeam?.color || '#8F9EB5';
        const start = getEffectiveStartTime(matchRecord.startTime);
        
        let statusColor = '#a5a5a5'; 
        let bg = `linear-gradient(145deg, rgba(255,255,255,0.10), rgba(0,0,0,0.3))`;
        // let bg = `color-mix(in srgb, ${oppColor} 15%, transparent)`; 
        let borderStyle = `1.5px dashed color-mix(in srgb, ${oppColor} 65%, transparent) `; 
        let tooltipText = `vs ${oppTeam?.name || "TBA"}\nBroadcast: ${start.toFormat('MMM d, h:mm a')}`;
        let bottomText = "-";
        let opacity = 0.85;


        if (matchRecord.isFinal || (isAdmin && (parseFloat(matchRecord.team1_vp) + parseFloat(matchRecord.team2_vp) > 1))) {
            opacity = 1; 
            const myVp = parseFloat(matchRecord.isTeam1 ? matchRecord.team1_vp : matchRecord.team2_vp) || 0;
            const oppVp = parseFloat(matchRecord.isTeam1 ? matchRecord.team2_vp : matchRecord.team1_vp) || 0;
            const myImp = parseFloat(matchRecord.isTeam1 ? matchRecord.team1_score : matchRecord.team2_score) || 0;
            const oppImp = parseFloat(matchRecord.isTeam1 ? matchRecord.team2_score : matchRecord.team1_score) || 0;
            const impDiff = myImp - oppImp;

            if (myVp > oppVp) { statusColor = WIN_COLOR; }
            else if (myVp < oppVp) { statusColor = LOSE_COLOR; }
            else { statusColor = '#FFD700'; }

            borderStyle = `1.5px solid color-mix(in srgb, ${oppColor} 65%, transparent) `; // Solid border of opponent's color when finished
            // bg = `linear-gradient(145deg, rgba(255, 255, 255, 0.19), rgba(255, 255, 255, 0.21))`;
            bg = `linear-gradient(145deg, rgba(255,255,255,0.15), rgba(0,0,0,0.5))`;
            bottomText = impDiff > 0 ? `+${impDiff}` : impDiff < 0 ? `${impDiff}` : '0';
            tooltipText = `vs ${oppTeam?.name || "TBA"}\nVP: ${myVp.toFixed(2)} - ${oppVp.toFixed(2)}\nIMPs: ${myImp} - ${oppImp}`;
        }

        if (matchRecord.result && matchRecord.result.trim() !== '') {
            tooltipText += `\nNOTE|${matchRecord.result}`;
        }

        const safeBoxId = matchRecord.id || `rr-${matchRecord.team1}-${matchRecord.team2}-${matchRecord.startTime}`;
        const handleMouseEnter = (e) => {
            if (isScrolling.current) return;

            const rect = e.currentTarget.getBoundingClientRect();
            setGlobalTooltip({
                visible: true,
                x: rect.left + (rect.width / 2), // Center X
                y: rect.top, // Top Y
                text: tooltipText,
                id: safeBoxId
            });
        };

        // const handleMouseLeave = () => {
        //     setGlobalTooltip(prev => ({ ...prev, visible: false }));
        // };

        return (
            <div 
                className="timeline-item" 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null })}
                onClick={(e) => { 
                    e.stopPropagation(); 
                    if (!isMobile || (globalTooltip.id === safeBoxId && globalTooltip.visible)) {
                        setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null });
                        if (matchRecord.id) {
                            if (focusId) setFocusTeamId(focusId); // Lock perspective!
                            setDetailedMatchId(matchRecord.id); 
                        }
                    } else {
                        handleMouseEnter(e);
                    }
                }}
                style={{
                    position: 'relative', cursor: 'pointer', opacity: opacity,
                    background: bg, border: borderStyle, borderRadius: 8,
                    padding: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                    width: 75, flexShrink: 0, boxSizing: 'border-box'
                }}
            >
                <TeamIcon team={oppTeam} size={22} opacity={0.75}/>
                <div style={{fontSize: 11, fontWeight: 'bold', color: statusColor, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap'}}>{bottomText}</div>
                {/* Note: The inline CSS tooltip has been deleted from here! */}
            </div>
        );
    };

    const Spacer = ({ height }) => <div style={{height: height, width: '100%', flexShrink: 0}} />;

    const StraightConnector = ({ width = 40 }) => (
        <div style={{display: 'flex', alignItems: 'center', height: 82, width: width}}>
            <div style={{width: '100%', height: 2, background: 'rgba(255,255,255,0.4)'}} />
        </div>
    );

    const BracketFork = ({ height }) => (
        <div style={{display: 'flex', alignItems: 'center', height: height, width: 40}}>
            <div style={{
                width: 20, height: '100%', 
                borderRight: `2px solid rgba(255,255,255,0.4)`, 
                borderTop: `2px solid rgba(255,255,255,0.4)`, 
                borderBottom: `2px solid rgba(255,255,255,0.4)`,
                borderTopRightRadius: 8, borderBottomRightRadius: 8
            }} />
            <div style={{width: 20, height: 2, background: 'rgba(255,255,255,0.4)'}} />
        </div>
    );

    // --- 3. BRACKET RENDERER ---
    const KnockoutBracket = ({ sectionId }) => {
        const matches = computedBrackets[sectionId] || {};
        const pfx = sectionId === 'main' ? 'm' : 'c'; // Prefix for match IDs
        
        const MatchBox = ({ matchId, label, topFallback, bottomFallback, isGrandFinal }) => {
            const m = matches[matchId] || {};
            const t1 = teams.find(t => t.id.toString() === m.team1?.toString());
            const t2 = teams.find(t => t.id.toString() === m.team2?.toString());
            
            const schedRecord = t1 && t2 ? schedule.find(s => 
                s.stage !== 'RR' && ((s.team1?.toString() === t1.id.toString() && s.team2?.toString() === t2.id.toString()) || (s.team1?.toString() === t2.id.toString() && s.team2?.toString() === t1.id.toString()))
            ) : null;
            
            const renderTeamRow = (t, score, isWinner, fallback) => (
                <div 
                    onClick={(e) => {
                        if (t) {
                            e.stopPropagation(); 
                            if (isScrolling.current) return;
                            
                            const boxId = schedRecord ? schedRecord.id : label;
                            
                            if (!isMobile || (globalTooltip.id === boxId && globalTooltip.visible)) {
                                setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null });
                                if (schedRecord && (schedRecord.isFinal || (isAdmin && (parseFloat(schedRecord.team1_vp)+parseFloat(schedRecord.team2_vp) > 1)))) {
                                    setFocusTeamId(t.id); // Lock perspective!
                                    setDetailedMatchId(schedRecord.id);
                                } else {
                                    setDetailedTeamId(t.id);
                                }
                            }
                        }
                    }}
                    
                    style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px', height: '50%', 
                        background: t?.color ? `color-mix(in srgb, ${t.color} ${isWinner ? '35%' : '10%'}, transparent)` : 'transparent', 
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        cursor: t ? 'pointer' : 'default' 
                    }}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: 6, opacity: isWinner===false ? 0.5 : 1}}>
                        {t ? <TeamIcon team={t} size={20} /> : <div style={{width: 20, height: 20, borderRadius: '50%', background: '#333'}} />}
                        <span style={{fontSize: 13, fontWeight: isWinner ? 'bold' : 'normal', color: t ? '#fff' : '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110}}>
                            {t?.name || fallback}
                        </span>
                    </div>
                    <span style={{fontSize: 14, fontWeight: 'bold', color: isWinner ? '#26A65B' : '#888'}}>{score || ''}</span>
                </div>
            );
            return (
                <div style={{display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', transform: isGrandFinal ? 'scale(1.15)' : 'none', transformOrigin: 'left center'}}>
                    <div style={{fontSize: 10, color: '#A0AEC0', textTransform: 'uppercase', textAlign: 'center', letterSpacing: 1, fontWeight: 'bold'}}>{label}</div>
                    <div 
                        onClick={(e) => {
                            if (schedRecord) {
                                e.stopPropagation();
                                 
                                if (isScrolling.current) return;
                                const boxId = schedRecord ? schedRecord.id : label;
                                
                                // NEW: Enforce visibility check
                                if (globalTooltip.id !== boxId || !globalTooltip.visible) {
                                    return; // Tooltip shows, click aborts
                                } 

                                setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null });
                                setDetailedMatchId(schedRecord.id);
                            }
                        }}
                        onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            let txt = "";

                            if (schedRecord) {
                                const start = getEffectiveStartTime(schedRecord.startTime);
                                const t1IsTeam1 = schedRecord.team1?.toString() === t1?.id?.toString();
                                const t1Vp = parseFloat(t1IsTeam1 ? schedRecord.team1_vp : schedRecord.team2_vp) || 0;
                                const t2Vp = parseFloat(t1IsTeam1 ? schedRecord.team2_vp : schedRecord.team1_vp) || 0;
                                const t1Imp = parseFloat(t1IsTeam1 ? schedRecord.team1_score : schedRecord.team2_score) || 0;
                                const t2Imp = parseFloat(t1IsTeam1 ? schedRecord.team2_score : schedRecord.team1_score) || 0;

                                txt = (schedRecord.isFinal || (isAdmin && (parseFloat(schedRecord.team1_vp)+parseFloat(schedRecord.team2_vp) > 1)))
                                    ? `Match Results\nVP: ${t1Vp.toFixed(2)} - ${t2Vp.toFixed(2)}\nIMPs: ${t1Imp} - ${t2Imp}` 
                                    : `Scheduled Match\nBroadcast: ${start.toFormat('MMM d, h:mm a')}`;

                                if (schedRecord.result && schedRecord.result.trim() !== '') {
                                    txt += `\nNOTE|${schedRecord.result}`;
                                }
                            } else {
                                // NEW: Tooltip for matches that haven't happened/been scheduled yet
                                txt = `${label}\nMatch Not Yet Scheduled`;
                            }
                            const boxId = schedRecord ? schedRecord.id : label;
                            setGlobalTooltip({ visible: true, x: rect.left + (rect.width / 2), y: rect.top, text: txt, id: boxId });
                        }}
                        onMouseLeave={() => setGlobalTooltip({ visible: false, x: 0, y: 0, text: '', id: null })}
                        style={{
                            background: 'rgba(30, 34, 45, 0.9)', border: isGrandFinal ? '2px solid #FFD700' : '1px solid rgba(255, 255, 255, 0.35)', 
                            borderRadius: 8, width: 180, height: 82, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 2,
                            cursor: schedRecord ? 'pointer' : 'default',
                            boxShadow: isGrandFinal ? '0 0 25px rgba(255,215,0,0.3)' : (schedRecord ? '0 4px 10px rgba(0,0,0,0.4)' : 'none')
                        }}
                    >
                        {renderTeamRow(t1, m.score1, m.winner === 'team1' ? true : m.winner === 'team2' ? false : null, topFallback)}
                        {renderTeamRow(t2, m.score2, m.winner === 'team2' ? true : m.winner === 'team1' ? false : null, bottomFallback)}
                    </div>
                </div>
            );
        };

        // Layout Math
        const cardHeight = 82; const gap = 40;
        const QF_DIST = cardHeight + gap; const SF_DIST = QF_DIST * 2; const CLAW_DIST = QF_DIST * 3; 

        return (
            <div style={{display: 'flex', flexDirection: 'row', padding: '40px 20px 80px 20px', width: 'max-content', margin: '0 auto'}}>
            {/* // <div style={{display: 'flex', flexDirection: 'row', padding: '40px 20px 80px 20px', overflowX: 'auto', justifyContent: 'center'}}> */}
                
                {/* COL 1: CLAW-INS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={18} />
                    <MatchBox matchId={`${pfx}pi1`} label={sectionId==='main' ? "Claw-in 1" : "Claw-in 3"} topFallback={sectionId==='main' ? "RR A 4th" : "RR A 8th"} bottomFallback={sectionId==='main' ? "RR B 5th" : "RR B 9th"} />
                    <Spacer height={CLAW_DIST - cardHeight} />
                    <MatchBox matchId={`${pfx}pi2`} label={sectionId==='main' ? "Claw-in 2" : "Claw-in 4"} topFallback={sectionId==='main' ? "RR A 5th" : "RR A 9th"} bottomFallback={sectionId==='main' ? "RR B 4th" : "RR B 8th"} />
                </div>

                {/* COL 2: CONNECTORS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={33} />
                    <StraightConnector />
                    <Spacer height={CLAW_DIST - cardHeight+18} />
                    <StraightConnector />
                </div>

                {/* COL 3: QUARTER FINALS */}
                <div style={{display: 'flex', flexDirection: 'column', gap: gap}}>
                    <MatchBox matchId={`${pfx}qf1`} label="Quarterfinal 1" topFallback={sectionId==='main' ? "RR A 1st" : "Loser CI 2"} bottomFallback={sectionId==='main' ? "Winner CI 1" : "Winner CI 3"} />
                    <MatchBox matchId={`${pfx}qf2`} label="Quarterfinal 2" topFallback={sectionId==='main' ? "RR A 3rd" : "RR A 7th"} bottomFallback={sectionId==='main' ? "RR B 2nd" : "RR B 6th"} />
                    <MatchBox matchId={`${pfx}qf3`} label="Quarterfinal 3" topFallback={sectionId==='main' ? "RR B 3rd" : "RR B 7th"} bottomFallback={sectionId==='main' ? "RR A 2nd" : "RR A 6th"} />
                    <MatchBox matchId={`${pfx}qf4`} label="Quarterfinal 4" topFallback={sectionId==='main' ? "Winner CI 2" : "Winner CI 4"} bottomFallback={sectionId==='main' ? "RR B 1st" : "Loser CI 1"} />
                </div>

                {/* COL 4: CONNECTORS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={57} />
                    <BracketFork height={QF_DIST+18} />
                    <Spacer height={134} />
                    <BracketFork height={QF_DIST+18} />
                </div>

                {/* COL 5: SEMI FINALS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={72} />
                    <MatchBox matchId={`${pfx}sf1`} label="Semifinal 1" topFallback="Winner QF 1" bottomFallback="Winner QF 2" />
                    <Spacer height={SF_DIST - cardHeight+12} />
                    <MatchBox matchId={`${pfx}sf2`} label="Semifinal 2" topFallback="Winner QF 3" bottomFallback="Winner QF 4" />
                </div>

                {/* COL 6: CONNECTORS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={130} />
                    <BracketFork height={SF_DIST+26} />
                </div>

                {/* COL 7: FINALS */}
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Spacer height={207} />
                    <MatchBox matchId={`${pfx}final`} label="Grand Final" topFallback="Winner SF 1" bottomFallback="Winner SF 2" isGrandFinal={true} />
                    <Spacer height={80} />
                    <div style={{opacity: 0.8, marginLeft: 20}}>
                        <MatchBox matchId={`${pfx}bronze`} label="3rd Place Match" topFallback="Loser SF 1" bottomFallback="Loser SF 2" />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="PageLayout" style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <SEO title="Standings" description="View live standings and results for Bridge Brawl 2026." url="/standings" />
            
            <style>{`
                /* Opaque, completely solid tooltips */
                .custom-tooltip {
                    visibility: hidden; opacity: 0; position: absolute; bottom: 120%; left: 50%;
                    transform: translateX(-50%) translateY(5px); background: #11131A;
                    border: 1px solid rgba(255,255,255,0.2); color: white; padding: 10px 14px;
                    border-radius: 8px; font-size: 12px; white-space: nowrap;
                    box-shadow: 0 10px 25px rgba(0,0,0,1); transition: all 0.15s ease; z-index: 999999; pointer-events: none;
                }
                .timeline-item:hover .custom-tooltip { visibility: visible; opacity: 1; transform: translateX(-50%) translateY(0); }

                .timeline-item:hover { 
                    opacity: 1 !important; /* CRITICAL: This allows the tooltip to escape the opacity trap! */
                    pointer-events: auto;
                }
                .standings-table { table-layout: fixed; width: 100%; }
                /* Increased padding to de-cramp the rows */
                .standings-table th, .standings-table td { padding: 14px 10px; }

                .standings-table th:first-child, .standings-table td:first-child { padding-left: 25px; }
                .standings-table th:last-child, .standings-table td:last-child { padding-right: 25px; }
                
                .CustomScroll::-webkit-scrollbar {
                    display: none;
                }
                /* Hide scrollbar for IE, Edge and Firefox */
                .CustomScroll {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }

                .detailed-board-table th { background: rgba(0,0,0,0.3); padding: 8px; text-align: center; color: #8F9EB5; font-size: 11px; border-bottom: 1px solid rgba(255,255,255,0.1); }
                .detailed-board-table td { padding: 8px; text-align: center; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.05); }

                .bracket-wrapper {
                    display: flex;
                    flex-direction: row;
                    padding: 40px 20px 80px 20px;
                    overflow-x: auto;
                    justify-content: flex-start; /* Naturally aligns left on mobile */
                }
                
                /* Center the bracket ONLY on wide screens where it won't overflow */
                @media (min-width: 1000px) {
                    .bracket-wrapper {
                        justify-content: center; 
                    }
                }

                @media (max-width: 900px) {
                    .standings-table th, .standings-table td { padding: 10px 4px; font-size: 12px; min-width: 750px; }
                    .hide-mobile { display: none; }
                    .col-team { width: 120px; }
                    .team-name-text { font-size: 11px; }
                    /* Timeline items wrap on really narrow screens */
                    .timeline-wrapper { flex-wrap: wrap !important; }
                }
            `}</style>

            <div className="ContentWrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: 1400, margin: '0 auto', width: '100%', paddingBottom: 20, paddingTop: 15 }}>
                
                {/* --- COMPACT PAGE HEADER --- */}
                <div style={{position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 0 15px 0', flexShrink: 0}}>
                    <h1 className="SectionTitle" style={{ margin: 0, fontSize: '2rem' }}>Tournament Results</h1>
                    
                    {/* ADMIN DROPDOWN (Absolute so it doesn't push layout) */}
                    
                </div>
                
                {/* --- MAIN TABS --- */}
                {!detailedTeamId && !detailedMatchId && (
                    <div style={{display: 'flex', justifyContent: 'center', marginBottom: 15, flexShrink: 0}}>
                        <div style={{display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: 30, padding: 4}}>
                            <button onClick={() => setMainTab('RR')} style={{background: mainTab === 'RR' ? '#6C5DD3' : 'transparent', color: mainTab === 'RR' ? '#fff' : '#A0AABF', border: 'none', padding: '8px 24px', borderRadius: 26, fontSize: 14, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>Round Robin</button>
                            <button onClick={() => setMainTab('KO')} style={{background: mainTab === 'KO' ? '#F39C12' : 'transparent', color: mainTab === 'KO' ? '#fff' : '#A0AABF', border: 'none', padding: '8px 24px', borderRadius: 26, fontSize: 14, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>Knockouts</button>
                        </div>
                    </div>
                )}

                {/* --- 1. ADMIN DETAILED VIEW --- */}
                {detailedMatchId && detailedMatchRecord && (() => {
                    // Original assignments
                    const origT1 = teams.find(t => t.id.toString() === detailedMatchRecord.team1?.toString());
                    const origT2 = teams.find(t => t.id.toString() === detailedMatchRecord.team2?.toString());
                    const start = getEffectiveStartTime(detailedMatchRecord.startTime);

                    // PERSPECTIVE SHIFT: Defaults to T1 unless focus specifically matches T2
                    const isFocusT1 = focusTeamId ? focusTeamId.toString() !== origT2?.id?.toString() : true;
                    
                    // The "Left Side" is now the Focus Team
                    const t1 = isFocusT1 ? origT1 : origT2;
                    const t2 = isFocusT1 ? origT2 : origT1;
                    
                    const vp1 = parseFloat(isFocusT1 ? detailedMatchRecord.team1_vp : detailedMatchRecord.team2_vp) || 0;
                    const vp2 = parseFloat(isFocusT1 ? detailedMatchRecord.team2_vp : detailedMatchRecord.team1_vp) || 0;
                    const imp1 = parseFloat(isFocusT1 ? detailedMatchRecord.team1_score : detailedMatchRecord.team2_score) || 0;
                    const imp2 = parseFloat(isFocusT1 ? detailedMatchRecord.team2_score : detailedMatchRecord.team1_score) || 0;

                    return (
                        <div className="GlassCard" style={{
                            display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', 
                            maxWidth: 1800, margin: '0 auto', width: '100%', flex: 1,
                            background: '#151520', // Unified dark background
                            border: '1px solid rgba(255,255,255,0.05)',
                            // Perspective border on the left edge based on focused team's color
                            borderLeft: `6px solid ${t1?.color || '#555'}`,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.6)'
                        }}>
                            
                            {/* STICKY ELEGANT SCOREBOARD HEADER */}
                            <div style={{
                                background: 'rgba(0,0,0,0.4)', padding: '20px 25px', 
                                borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, // Softer border line
                                display: 'flex', flexDirection: 'column', gap: 15, zIndex: 10
                            }}>
                                <button 
                                    onClick={() => { setDetailedMatchId(""); setActiveBoardPlay(null); setFocusTeamId(null); }} 
                                    style={{
                                        alignSelf: 'flex-start', background: 'rgba(108, 93, 211, 0.15)', border: '1px solid rgba(108, 93, 211, 0.4)', 
                                        color: '#A395E9', padding: '6px 14px', borderRadius: 20, cursor: 'pointer', 
                                        fontSize: 12, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6, transition: '0.2s'
                                    }}
                                >
                                    <span>&larr;</span> Back to {mainTab === 'KO' ? 'Bracket' : 'Standings'}
                                </button>

                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15}}>
                                    {/* Team 1 & VP */}
                                    <div style={{display: 'flex', alignItems: 'center', gap: 12, flex: 1}}>
                                        <TeamIcon team={t1} size={44} clickable={true} />
                                        <div style={{display: 'flex', flexDirection: 'column'}}>
                                            <strong style={{fontSize: 20, color: t1?.color || '#fff', lineHeight: 1.2}}>{t1?.name}</strong>
                                            {(detailedMatchRecord.isFinal || (isAdmin  && (parseFloat(detailedMatchRecord.team1_vp)+parseFloat(detailedMatchRecord.team2_vp)> 1))) && (
                                                <span style={{
                                                    fontSize: 16, fontWeight: 'bold', 
                                                    color: vp1 > vp2 ? WIN_COLOR : (vp1 < vp2 ? LOSE_COLOR : TIE_COLOR)
                                                }}>
                                                    {vp1.toFixed(2)} VP
                                                </span>
                                            )}
                                        </div>
                                        {isFocusT1 ? (
                                                <div style={{marginLeft: 15}}>
                                                    <MiniRosterGrid 
                                                        tNS={!isFocusT1 ? allLineups[detailedMatchRecord.id]?.team2Lineup : allLineups[detailedMatchRecord.id]?.team1Lineup} 
                                                        tEW={isFocusT1 ? allLineups[detailedMatchRecord.id]?.team1Lineup : allLineups[detailedMatchRecord.id]?.team2Lineup} 
                                                        cNS={t1?.color || '#555'} 
                                                        cEW={t1?.color || '#555'} 
                                                        // title="Table 1 (home)" 
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{marginRight: 15}}>
                                                    <MiniRosterGrid 
                                                        tNS={isFocusT1 ? allLineups[detailedMatchRecord.id]?.team1Lineup : allLineups[detailedMatchRecord.id]?.team2Lineup} 
                                                        tEW={!isFocusT1 ? allLineups[detailedMatchRecord.id]?.team2Lineup : allLineups[detailedMatchRecord.id]?.team1Lineup} 
                                                        cNS={t1?.color || '#555'} 
                                                        cEW={t1?.color || '#555'} 
                                                        // title="Table 2 (home)" 
                                                    />
                                                </div>
                                            )
                                        }
                                    </div>
                                    
                                    {/* Center: BIG IMPs & VOD Button */}
                                    <div style={{flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                        { (detailedMatchRecord.isFinal || (isAdmin && (parseFloat(detailedMatchRecord.team1_vp) + parseFloat(detailedMatchRecord.team2_vp) > 1))) ? (
                                            <>
                                                {/* High-visibility IMP score */}
                                                <div style={{fontSize: 26, fontWeight: '700', color: '#FFF', letterSpacing: 1}}>
                                                    {imp1} <span style={{fontSize: 16, color: '#8F9EB5', fontWeight: 'bold', margin: '0 8px'}}>vs.</span> {imp2}
                                                </div>
                                                
                                                {/* VOD Toggle Button */}
                                                {(detailedMatchRecord.vodUrl || (detailedMatchRecord.links && detailedMatchRecord.links.length > 0)) && (
                                                    <button 
                                                        onClick={() => setActiveTeamVodId(activeTeamVodId === detailedMatchRecord.id ? null : detailedMatchRecord.id)}
                                                        style={{
                                                            marginTop: 8,
                                                            background: 'rgba(255,255,255,0.05)',
                                                            color:  '#fff',
                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                            padding: '6px 16px', borderRadius: 20, fontSize: 11, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'
                                                        }}
                                                    >
                                                        {activeTeamVodId === detailedMatchRecord.id ? '▼ Hide Coverage' : '▶ Show Coverage'}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{fontSize: 15, color: '#A395E9', fontWeight: 'bold'}}>Upcoming: {start.toFormat('MMM d, h:mm a')}</div>
                                        )}
                                    </div>

                                    {/* Team 2 & VP */}
                                    <div style={{display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'flex-end'}}>
                                        {!isFocusT1 ? (
                                                <div style={{marginLeft: 15}}>
                                                    <MiniRosterGrid 
                                                        tNS={isFocusT1 ? allLineups[detailedMatchRecord.id]?.team2Lineup : allLineups[detailedMatchRecord.id]?.team1Lineup} 
                                                        tEW={!isFocusT1 ? allLineups[detailedMatchRecord.id]?.team1Lineup : allLineups[detailedMatchRecord.id]?.team2Lineup} 
                                                        cNS={t2?.color || '#555'} 
                                                        cEW={t2?.color || '#555'} 
                                                        // title="Table 1 (away)" 
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{marginRight: 15}}>
                                                    <MiniRosterGrid 
                                                        tNS={!isFocusT1 ? allLineups[detailedMatchRecord.id]?.team1Lineup : allLineups[detailedMatchRecord.id]?.team2Lineup} 
                                                        tEW={isFocusT1 ? allLineups[detailedMatchRecord.id]?.team2Lineup : allLineups[detailedMatchRecord.id]?.team1Lineup} 
                                                        cNS={t2?.color || '#555'} 
                                                        cEW={t2?.color || '#555'} 
                                                        // title="Table 2 (away)" 
                                                    />
                                                </div>
                                            )
                                        }
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                            <strong style={{fontSize: 20, color: t2?.color || '#fff', lineHeight: 1.2}}>{t2?.name}</strong>
                                            {(detailedMatchRecord.isFinal || (isAdmin  && (parseFloat(detailedMatchRecord.team1_vp) + parseFloat(detailedMatchRecord.team2_vp) > 1))) && (
                                                <span style={{
                                                    fontSize: 16, fontWeight: 'bold', 
                                                    color: vp2 > vp1 ? WIN_COLOR : (vp2 < vp1 ? LOSE_COLOR : TIE_COLOR)
                                                }}>
                                                    {vp2.toFixed(2)} VP
                                                </span>
                                            )}
                                        </div>
                                        <TeamIcon team={t2} size={44} clickable={true} />
                                    </div>
                                </div>
                            </div>

                            {/* SCROLLING MATCH CONTENT */}
                            {/* <TickedObject/> */}
                            <div className="CustomScroll" style={{ flex: 1, overflowY: 'auto', padding: 25 }}>
                                {!(detailedMatchRecord.isFinal || (isAdmin && (parseFloat(detailedMatchRecord.team1_vp)+ parseFloat(detailedMatchRecord.team2_vp) > 1))) ? (() => {
                                    return (<MatchCountdown getEffectiveStartTime={getEffectiveStartTime} detailedMatchRecord={detailedMatchRecord} userRole={userRole} t1={t1} t2={t2} />);
                                })() : (
                                <>
                                    {/* VOD EMBED (Now hidden by default!) */}
                                    {activeTeamVodId === detailedMatchRecord.id && (detailedMatchRecord.vodUrl || (detailedMatchRecord.links && detailedMatchRecord.links.length > 0)) && (
                                        <div style={{ 
                                            width: '100%', background: '#0D0E15', 
                                            borderRadius: 16, overflow: 'hidden', 
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid #333',
                                            marginBottom: 20 
                                        }}>
                                            <MultiStreamPlayer event={detailedMatchRecord} />
                                        </div>
                                    )}

                                    {/* Board Table */}
                                    {detailedMatchRecord.parsedBoards && detailedMatchRecord.parsedBoards.length > 0 && (
                                        <div style={{overflowX: 'auto', paddingBottom: 15}}>
                                            {(() => {
                                                let totalPlus = 0;
                                                let totalMinus = 0;

                                                return (
                                                    <table className="detailed-board-table" style={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 600}}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{width: '40px', paddingBottom: 10}}>Bd</th>
                                                                <th style={{paddingBottom: 10}}>Home</th>
                                                                <th style={{paddingBottom: 10}}>Away</th>
                                                                <th style={{width: '100px', paddingBottom: 10}}>Score<br/><span style={{fontSize: 9, color: '#666'}}>(Home | Away)</span></th>
                                                                <th style={{width: '60px', paddingBottom: 10, color: WIN_COLOR}}>+ IMPs</th>
                                                                <th style={{width: '60px', paddingBottom: 10, color: LOSE_COLOR}}>- IMPs</th>
                                                                <th style={{width: '80px', paddingBottom: 10}}>Replay</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {detailedMatchRecord.parsedBoards.map((b, i) => {
                                                                const myBoardImps = isFocusT1 ? b.imps1 - b.imps2 : b.imps2 - b.imps1;
                                                                if (myBoardImps > 0) totalPlus += myBoardImps;
                                                                if (myBoardImps < 0) totalMinus += Math.abs(myBoardImps);

                                                                const nsContract = isFocusT1 ? b.room1 : b.room2;
                                                                const ewContract = isFocusT1 ? b.room2 : b.room1;
                                                                
                                                                const r1 = b.raw1 !== undefined ? b.raw1 : b.score1;
                                                                const r2 = b.raw2 !== undefined ? b.raw2 : b.score2;
                                                                const nsScore = isFocusT1 ? r1 : r2;
                                                                const ewScore = isFocusT1 ? r2 : r1;
                                                                
                                                                const boardId = `single-${i}`;
                                                                const isActive = activeBoardPlay === boardId;
                                                                
                                                                return (
                                                                    <React.Fragment key={i}>
                                                                        <tr style={{opacity: b.segment === 'Omitted' ? 0.4 : 1, background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                                                                            <td style={{color: '#8F9EB5'}}>{b.boardNum}</td>
                                                                            <td>{formatContract(nsContract)}</td>
                                                                            <td>{formatContract(ewContract)}</td>
                                                                            <td style={{fontSize: 11, color: '#A0AABF'}}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '65px', margin: '0 auto' }}>
                                                                                    <span style={{ flex: 1, textAlign: 'right', color:  '#fff' }}>{nsScore || 0}</span>
                                                                                    <span style={{ color: 'rgba(255,255,255,0.2)', padding: '0 4px' }}>|</span>
                                                                                    <span style={{ flex: 1, textAlign: 'right', color: '#fff' }}>{ewScore || 0}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td style={{fontWeight: 'bold', color: myBoardImps > 0 ? WIN_COLOR : TIE_COLOR}}>{myBoardImps > 0 ? myBoardImps : '-'}</td>
                                                                            <td style={{fontWeight: 'bold', color: myBoardImps < 0 ? LOSE_COLOR : TIE_COLOR}}>{myBoardImps < 0 ? Math.abs(myBoardImps) : '-'}</td>
                                                                            <td>
                                                                                <button 
                                                                                    onClick={() => setActiveBoardPlay(isActive ? null : boardId)} 
                                                                                    style={{ background: 'rgba(108, 93, 211, 0.15)', color: '#A395E9', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 'bold', cursor: 'pointer' }}
                                                                                >
                                                                                    {isActive ? 'x Close' : 'Expand'}
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                        <tr style={{ display: isActive ? 'table-row' : 'none' }}>
                                                                            <td colSpan={7} style={{padding: 0}}>
                                                                                <div style={{padding: '15px 0', borderBottom: '2px solid rgba(108, 93, 211, 0.3)'}}>
                                                                                    <HandAnalyzer 
                                                                                        board={b} 
                                                                                        isActive={isActive} 
                                                                                        t1Data={allLineups[detailedMatchRecord.id]?.team1Lineup} 
                                                                                        t2Data={allLineups[detailedMatchRecord.id]?.team2Lineup} 
                                                                                        t1Color={origT1?.color} 
                                                                                        t2Color={origT2?.color}
                                                                                        swapRooms={!isFocusT1}
                                                                                    />
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    </React.Fragment>
                                                                )
                                                            })}
                                                            <tr style={{background: 'rgba(0,0,0,0.4)', borderTop: '2px solid rgba(255,255,255,0.1)'}}>
                                                                <td colSpan={4} style={{textAlign: 'right', fontWeight: 'bold', paddingRight: 20, color: '#fff', fontSize: 14}}>TOTAL IMPs:</td>
                                                                <td style={{color: WIN_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalPlus}</td>
                                                                <td style={{color: LOSE_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalMinus}</td>
                                                                <td></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {detailedTeamId && !detailedMatchId && (() => {
                    const selectedTeam = teams.find(t => t.id.toString() === detailedTeamId.toString());
                    if (!selectedTeam) return null;

                    const relevantMatches = schedule.filter(m => {
                        const isParticipant = m.team1?.toString() === detailedTeamId.toString() || m.team2?.toString() === detailedTeamId.toString();
                        if (!isParticipant) return false;
                        if (mainTab === 'KO') return m.stage !== 'RR';
                        return m.stage === 'RR' || !m.stage;
                    }).sort((a, b) => DateTime.fromISO(a.startTime) - DateTime.fromISO(b.startTime));

                    const rrStats = detailedTeamData?.team?.id === detailedTeamId ? detailedTeamData : null;

                    return (
                        <div className="GlassCard" style={{ 
                            display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', 
                            maxWidth: 1800, margin: '0 auto', width: '100%', flex: 1,
                            background: '#151520', // Unified dark background
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderLeft: `6px solid ${selectedTeam?.color || '#555'}`, // Perspective accent
                            boxShadow: '0 10px 40px rgba(0,0,0,0.6)'
                        }}>
                            
                            {/* STICKY ELEGANT HEADER */}
                            <div style={{
                                background: 'rgba(0,0,0,0.4)', padding: '20px 25px', 
                                borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, // Softer border line
                                display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 15, zIndex: 10
                            }}>
                                <button 
                                    onClick={() => { setDetailedTeamId(""); setDetailedMatchId(""); setActiveBoardPlay(null); setActiveTeamVodId(null); setFocusTeamId(null); }}
                                    style={{
                                        background: 'rgba(108, 93, 211, 0.15)', border: '1px solid rgba(108, 93, 211, 0.4)', 
                                        color: '#A395E9', padding: '6px 14px', borderRadius: 20, cursor: 'pointer', 
                                        fontSize: 12, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6, transition: '0.2s'
                                    }}
                                >
                                    <span>&larr;</span> Back to {mainTab === 'KO' ? 'Bracket' : 'Standings'}
                                </button>

                                <div style={{display: 'flex', alignItems: 'center', gap: 15, marginLeft: 'auto'}}>
                                    <TeamIcon team={selectedTeam} size={40} clickable={true} />
                                    <div>
                                        <h2 style={{margin: 0, fontSize: 20}}>{selectedTeam.name}</h2>
                                        {rrStats && mainTab === 'RR' && (
                                            <div style={{color: '#8F9EB5', fontSize: 13, marginTop: 4}}>
                                                Average VP: <strong style={{color: '#FFD700'}}>{rrStats.avgVp.toFixed(2)}</strong>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* SCROLLING MATCH LIST */}
                            <div className="CustomScroll" style={{ flex: 1, overflowY: 'auto', padding: 25 }}>
                                {relevantMatches.length === 0 ? (
                                    <div style={{textAlign: 'center', padding: '50px 20px', background: 'rgba(0,0,0,0.2)', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.1)'}}>
                                        <div style={{fontSize: 32, marginBottom: 12}}>📅</div>
                                        <h3 style={{color: '#fff', margin: '0 0 8px 0'}}>Matches Not Found</h3>
                                        <p style={{color: '#8F9EB5', margin: 0, fontSize: 14}}>
                                            {mainTab === 'KO' ? "The KO schedule is not yet published." : "No RR matches scheduled yet."}
                                        </p>
                                    </div>
                                ) : (
                                    relevantMatches.map((match, idx) => {
                                        const isT1 = match.team1?.toString() === detailedTeamId.toString();
                                        const oppId = isT1 ? match.team2 : match.team1;
                                        const oppTeam = teams.find(t => t.id.toString() === oppId?.toString());
                                        const start = getEffectiveStartTime(match.startTime);
                                        const myVp = parseFloat(isT1 ? match.team1_vp : match.team2_vp) || 0;
                                        const oppVp = parseFloat(isT1 ? match.team2_vp : match.team1_vp) || 0;
                                        const myImp = parseFloat(isT1 ? match.team1_score : match.team2_score) || 0;
                                        const oppImp = parseFloat(isT1 ? match.team2_score : match.team1_score) || 0;

                                        const t1Data = allLineups[match.id]?.team1Lineup || {};
                                        const t2Data = allLineups[match.id]?.team2Lineup || {};

                                        // Map the data to the correct side
                                        const selectedTeamData = isT1 ? t1Data : t2Data;
                                        const oppTeamData = isT1 ? t2Data : t1Data;

                                        return (
                                            <div key={idx} style={{background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid rgba(255,255,255,0.05)'}}>
                                                {/* Match Row Header */}
                                                
                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, width: '100%'}}>
    
                                                    {/* --- LEFT: Selected Team --- */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 10, flex: 1}}>
                                                        <TeamIcon team={selectedTeam} size={30} clickable={true} />
                                                        <strong style={{fontSize: 16}}>{selectedTeam?.name || "TBA"}</strong>
                                                        <div style={{marginLeft: 'auto'}}>
                                                            <MiniRosterGrid 
                                                                tNS={selectedTeamData} 
                                                                tEW={selectedTeamData} 
                                                                cNS={selectedTeam?.color || '#555'} 
                                                                cEW={selectedTeam?.color || '#555'} 
                                                            />
                                                        </div>
                                                    </div>

                                                

                                                    {/* --- CENTER: Scores & VOD Dropdown --- */}
                                                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8}}>
                                                        {(match.isFinal || (isAdmin && (parseFloat(match.team1_vp) + parseFloat(match.team2_vp) > 1))) ? (
                                                            <>
                                                                <div>
                                                                    <span style={{fontSize: 18, fontWeight: 'bold', color: myVp > oppVp ? WIN_COLOR : myVp < oppVp ? LOSE_COLOR : TIE_COLOR}}>{myVp.toFixed(2)} VP</span>
                                                                    
                                                                </div>
                                                                <div><span style={{fontSize: 12, color: '#888', marginLeft: 10}}>{myImp} - {oppImp} IMPs</span></div>
                                                                {(match.vodUrl || (match.links && match.links.length > 0)) && (
                                                                    <button 
                                                                        onClick={() => setActiveTeamVodId(activeTeamVodId === match.id ? null : match.id)}
                                                                        style={{
                                                                            background: 'rgba(255,255,255,0.05)',
                                                                            color: '#fff',
                                                                            border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 'bold', cursor: 'pointer'
                                                                        }}>
                                                                        {activeTeamVodId === match.id ? '▼ Hide Coverage' : '▶ Show Coverage'}
                                                                    </button>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div style={{fontSize: 13, color: '#A395E9', fontWeight: 'bold'}}>{start.toFormat('MMM d, h:mm a')}</div>
                                                        )}
                                                    </div>

                                                    {/* --- RIGHT: Opponent Team --- */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 10, flex: 1, justifyContent: 'flex-end'}}>
                                                        <div style={{marginRight: 'auto'}}>
                                                            <MiniRosterGrid 
                                                                tNS={oppTeamData} 
                                                                tEW={oppTeamData} 
                                                                cNS={oppTeam?.color || '#555'} 
                                                                cEW={oppTeam?.color || '#555'} 
                                                            />
                                                        </div>
                                                        <span style={{color: '#8F9EB5', fontSize: 11, textTransform: 'uppercase'}}>vs</span>
                                                        <strong style={{fontSize: 16}}>{oppTeam?.name || "TBA"}</strong>
                                                        <TeamIcon team={oppTeam} size={30} clickable={true} />
                                                    </div>
                                                    
                                                </div>

                                                {/* Inline VOD */}
                                                {activeTeamVodId === match.id && (
                                                    <div style={{ marginBottom: 20, background: '#0D0E15', borderRadius: 12, overflow: 'hidden', border: '1px solid #333' }}>
                                                        <MultiStreamPlayer event={match} />
                                                    </div>
                                                )}

                                                {/* Board Table */}
                                                {match.parsedBoards && match.parsedBoards.length > 0 && (
                                                    <div style={{overflowX: 'auto', paddingBottom: 15}}>
                                                    {(() => {
                                                        let totalPlus = 0;
                                                        let totalMinus = 0;

                                                        return (
                                                            <table className="detailed-board-table" style={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 600}}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{width: '40px', paddingBottom: 10}}>Bd</th>
                                                                        <th style={{paddingBottom: 10}}>Home</th>
                                                                        <th style={{paddingBottom: 10}}>Away</th>
                                                                        <th style={{width: '100px', paddingBottom: 10}}>Score<br/><span style={{fontSize: 9, color: '#666'}}>(Home | Away)</span></th>
                                                                        <th style={{width: '60px', paddingBottom: 10, color: WIN_COLOR}}>+ IMPs</th>
                                                                        <th style={{width: '60px', paddingBottom: 10, color: LOSE_COLOR}}>- IMPs</th>
                                                                        <th style={{width: '80px', paddingBottom: 10}}>Replay</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {match.parsedBoards.map((b, i) => {
                                                                        const myBoardImps = isT1 ? b.imps1 - b.imps2 : b.imps2 - b.imps1;
                                                                        if (myBoardImps > 0) totalPlus += myBoardImps;
                                                                        if (myBoardImps < 0) totalMinus += Math.abs(myBoardImps);

                                                                        const nsContract = isT1 ? b.room1 : b.room2;
                                                                        const ewContract = isT1 ? b.room2 : b.room1;
                                                                        
                                                                        const r1 = b.raw1 !== undefined ? b.raw1 : b.score1;
                                                                        const r2 = b.raw2 !== undefined ? b.raw2 : b.score2;
                                                                        const nsScore = isT1 ? r1 : r2;
                                                                        const ewScore = isT1 ? r2 : r1;
                                                                        
                                                                        const boardId = `${idx}-${i}`;
                                                                        const isActive = activeBoardPlay === boardId;
                                                                        
                                                                        return (
                                                                            <React.Fragment key={i}>
                                                                                <tr style={{opacity: b.segment === 'Omitted' ? 0.4 : 1, background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                                                                                    <td style={{color: '#8F9EB5'}}>{b.boardNum}</td>
                                                                                    <td>{formatContract(nsContract)}</td>
                                                                                    <td>{formatContract(ewContract)}</td>
                                                                                    <td style={{fontSize: 11, color: '#A0AABF'}}>
                                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '65px', margin: '0 auto' }}>
                                                                                            <span style={{ flex: 1, textAlign: 'right', color:  '#fff' }}>{nsScore || 0}</span>
                                                                                            <span style={{ color: 'rgba(255,255,255,0.2)', padding: '0 4px' }}>|</span>
                                                                                            <span style={{ flex: 1, textAlign: 'right', color: '#fff' }}>{ewScore || 0}</span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td style={{fontWeight: 'bold', color: myBoardImps > 0 ? WIN_COLOR : TIE_COLOR}}>{myBoardImps > 0 ? myBoardImps : '-'}</td>
                                                                                    <td style={{fontWeight: 'bold', color: myBoardImps < 0 ? LOSE_COLOR : TIE_COLOR}}>{myBoardImps < 0 ? Math.abs(myBoardImps) : '-'}</td>
                                                                                    <td>
                                                                                        <button 
                                                                                            onClick={() => setActiveBoardPlay(isActive ? null : boardId)} 
                                                                                            style={{ background: 'rgba(108, 93, 211, 0.15)', color: '#A395E9', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 'bold', cursor: 'pointer' }}
                                                                                        >
                                                                                            {isActive ? 'x Close' : 'Expand'}
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                                <tr style={{ display: isActive ? 'table-row' : 'none' }}>
                                                                            <td colSpan={7} style={{padding: 0}}>
                                                                                <div style={{padding: '15px 0', borderBottom: '2px solid rgba(108, 93, 211, 0.3)'}}>
                                                                                    <HandAnalyzer 
                                                                                        board={b} 
                                                                                        isActive={isActive} 
                                                                                        t1Data={allLineups[match.id]?.team1Lineup} 
                                                                                        t2Data={allLineups[match.id]?.team2Lineup} 
                                                                                        t1Color={isT1 ? selectedTeam?.color : oppTeam?.color} 
                                                                                        t2Color={isT1 ? oppTeam?.color : selectedTeam?.color} 
                                                                                        swapRooms={!isT1}
                                                                                    />
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                            </React.Fragment>
                                                                        )
                                                                    })}
                                                                    <tr style={{background: 'rgba(0,0,0,0.4)', borderTop: '2px solid rgba(255,255,255,0.1)'}}>
                                                                        <td colSpan={4} style={{textAlign: 'right', fontWeight: 'bold', paddingRight: 20, color: '#fff', fontSize: 14}}>TOTAL IMPs:</td>
                                                                        <td style={{color: WIN_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalPlus}</td>
                                                                        <td style={{color: LOSE_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalMinus}</td>
                                                                        <td></td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        );
                                                    })()}
                                                </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* --- 2. ROUND ROBIN VIEW --- */}
                {/* Notice: flex: 1 and overflow: hidden locks the card. The CustomScroll inside handles the table overflow. */}
                {!detailedTeamId && !detailedMatchId && mainTab === 'RR' && processedStandings[rrTab] && (
                    <div className="GlassCard" style={{
                        display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', 
                        background: '#151520', // Unified dark background
                        border: '1px solid rgba(255,255,255,0.08)',
                        maxWidth: 1200, margin: '0 auto 0 auto', 
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                        width: '100%', flex: 1, borderRadius: 16
                    }}>
                        {/* Fixed Header */}
                        <div style={{background: 'rgba(0,0,0,0.4)', padding: '16px 25px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 15, alignItems: 'center', flexShrink: 0}}>
                            <button onClick={() => setRrTab('A')} style={{background: rrTab === 'A' ? 'rgba(255,255,255,0.15)' : 'transparent', color: rrTab === 'A' ? '#fff' : '#8F9EB5', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>Round Robin 1</button>
                            <button onClick={() => setRrTab('B')} style={{background: rrTab === 'B' ? 'rgba(255,255,255,0.15)' : 'transparent', color: rrTab === 'B' ? '#fff' : '#8F9EB5', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>Round Robin 2</button>
                        </div>
                        
                        {/* Scrollable Inner Body */}
                        <div className="CustomScroll" style={{flex: 1, overflowX: 'auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch'}} onScroll={() => setGlobalTooltip(prev => ({ ...prev, visible: false }))}>
                            <table className="standings-table" style={{borderCollapse: 'collapse', textAlign: 'left', color: '#fff', minWidth: '750px'}}>
                                <thead style={{position: 'sticky', top: 0, zIndex: 10, background: '#151520', boxShadow: '0 4px 15px rgba(0,0,0,0.4)'}}>
                                    <tr style={{borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#E0E0E0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1}}>
                                        <th style={{width: 30, textAlign: 'center'}}>#</th>
                                        <th className="col-team" style={{width: 200}}>Team</th>
                                        <th style={{paddingLeft: 10}}></th> {/* Empty Timeline Header */}
                                        <th style={{width: 70, textAlign: 'center'}} title="Matches Played">Played:</th>
                                        <th 
                                            className="hide-mobile" 
                                            style={{width: 100, textAlign: 'center', cursor: 'pointer', userSelect: 'none', transition: '0.2s'}} 
                                            onClick={() => setExtraColIdx(prev => (prev + 1) % 4)}
                                            title="Click to cycle metrics"
                                        >
                                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5}}>
                                                {extraColLabels[extraColIdx]}
                                            </div>
                                        </th>
                                        {/* <th className="hide-mobile" style={{width: 70, textAlign: 'center'}} title="Total Victory Points">Total VPs</th> */}
                                        <th style={{width: 70, textAlign: 'center'}} title="Average Victory Points">Avg VP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedStandings[rrTab].map((teamData, index) => {
                                        const isTopSeed = index < 4; 
                                        const rankColor = isTopSeed ? topRankColors[index] : '#888';
                                        // const rowBg = isTopSeed ? topRankBgs[index] : 'transparent';
                                        const rowBg = index%2 === 1 ? '#FFFFFF08' : 'transparent';

                                        return (
                                            <tr key={teamData.id} style={{borderBottom: '1px solid rgba(255, 255, 255, 0.38)', background: rowBg}}>
                                                <td style={{textAlign: 'center', fontWeight: 'bold', color: rankColor, fontSize: 18}}>
                                                    {index + 1}
                                                </td>
                                                <td 
                                                    className="col-team" 
                                                    onClick={() => { setDetailedTeamId(teamData.id); }}
                                                    style={{cursor: 'pointer' }}
                                                >
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                                                        <TeamIcon team={teamData.team} size={28} />
                                                        <span className="team-name-text" style={{fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isTopSeed ? '#fff' : '#ccc'}}>
                                                            {teamData.team?.name || "TBA"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{padding: '10px 4px'}}>
                                                    {/* CRITICAL: flexWrap must be 'nowrap' to force horizontal scrolling */}
                                                    <div className="timeline-wrapper" style={{display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center'}}>
                                                        {teamData.timeline.map((match, i) => (
                                                            <TimelineItem key={i} matchRecord={match} focusId={teamData.id} />
                                                        ))}
                                                    </div>
                                                </td>
                                                <td style={{textAlign: 'center', color: '#ccc', fontSize: 14}}>{teamData.played}</td>
                                                <td className="hide-mobile" style={{textAlign: 'center', color: '#888', fontSize: 14}}>
                                                    {extraColIdx === 0 ? teamData.totalVp.toFixed(2) :
                                                     extraColIdx === 1 ? teamData.totalImpsSwung :
                                                     extraColIdx === 2 ? teamData.impsPerMatch.toFixed(2) :
                                                     teamData.impsPerBoard.toFixed(2)}
                                                </td>
                                                {/* <td className="hide-mobile" style={{textAlign: 'center', color: '#888', fontSize: 14}}>{teamData.totalVp.toFixed(2)}</td> */}
                                                <td style={{textAlign: 'center', fontWeight: 'bold', fontSize: 18, color: rankColor}}>{teamData.avgVp.toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Fixed Empty Footer */}
                        <div style={{height: 15, background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, borderBottomLeftRadius: 16, borderBottomRightRadius: 16}}></div>
                    </div>
                )}

                {/* --- 3. KNOCKOUT VIEW --- */}
                {!detailedTeamId && !detailedMatchId && mainTab === 'KO' && (
                    <div className="GlassCard" style={{display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', background: 'linear-gradient(145deg, #151520 100%, rgba(33, 38, 53, 1.0) 100%)', flex: 1}}>
                        
                        <div style={{background: 'rgba(0,0,0,0.3)', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', gap: 15, alignItems: 'center', borderTopLeftRadius: 16, borderTopRightRadius: 16}}>
                            <button onClick={() => setKoTab('main')} style={{background: koTab === 'main' ? 'rgba(255,255,255,0.15)' : 'transparent', color: koTab === 'main' ? '#fff' : '#8F9EB5', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>🏆 Championship Bracket</button>
                            <button onClick={() => setKoTab('consolation')} style={{background: koTab === 'consolation' ? 'rgba(255,255,255,0.15)' : 'transparent', color: koTab === 'consolation' ? '#fff' : '#8F9EB5', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'}}>🛡️ Challengers Bracket</button>
                        </div>
                        
                        <div className="CustomScroll" style={{flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch'}}>
                            {/* Call as a function to prevent DOM destruction on tooltip hover! */}
                            {KnockoutBracket({ sectionId: koTab })}
                        </div>

                    </div>
                )}
            </div>
            {/* --- GLOBAL FLOATING TOOLTIP --- */}
            {globalTooltip.visible && (() => {
                const lines = globalTooltip.text.split('\n');
                const header = lines[0];
                const sub = lines[1];
                const extra = lines[2] && !lines[2].startsWith('NOTE|') ? lines[2] : null;
                
                const noteLine = lines.find(l => l.startsWith('NOTE|'));
                const note = noteLine ? noteLine.replace('NOTE|', '') : null;

                return (
                    <div style={{
                        position: 'fixed', // Anchors to the browser window, ignoring all layout traps
                        top: globalTooltip.y - 8, // Shifts it 8px above the hovered element
                        left: globalTooltip.x,
                        transform: 'translate(-50%, -100%)', // Centers it horizontally, pulls it 100% upwards
                        background: '#11131A',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'white',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 10px 25px rgba(0,0,0,1)',
                        zIndex: 999999, // Safely above everything
                        pointerEvents: 'none' // Prevents flickering if the mouse accidentally hits it
                    }}>
                        <div style={{fontWeight: 'bold', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: 4, marginBottom: 4}}>
                            {header}
                        </div>
                        <div style={{color: '#FFD700'}}>
                            {sub}
                        </div>
                        {extra && (
                            <div style={{color: '#8F9EB5'}}>
                                {extra}
                            </div>
                        )}
                        {note && (
                            <div style={{color: '#E0E0E0', marginTop: 6, paddingTop: 6, borderTop: '1px dashed rgba(255,255,255,0.2)', fontStyle: 'italic', whiteSpace: 'normal', maxWidth: 220, lineHeight: 1.4}}>
                                * {note}
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

const MatchCountdown = ({getEffectiveStartTime, detailedMatchRecord, userRole, t1, t2}) => {
    const [now, setNow] = useState(DateTime.now());

    React.useEffect(() => {
        const timer = setInterval(() => setNow(DateTime.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const rightNow = now;
    const matchStart = getEffectiveStartTime(detailedMatchRecord.startTime);
    
    // SECRECY FIX: Subtract 15m to unlock exactly at T+0 for logged-out users
    const liveThreshold = userRole ? matchStart : matchStart.minus({ minutes: 15 });
    const matchEnd = detailedMatchRecord.endTime ? getEffectiveStartTime(detailedMatchRecord.endTime) : matchStart.plus({ hours: 2, minutes: 30 });
    
    const isLive = rightNow >= liveThreshold && rightNow <= matchEnd;
    const isPast = rightNow > matchEnd;
    const isUpcoming = rightNow < liveThreshold;
    const hasMedia = detailedMatchRecord.vodUrl || (detailedMatchRecord.links && detailedMatchRecord.links.length > 0);

    // Countdown Helper
    const diff = matchStart.diff(rightNow, ['days', 'hours', 'minutes']).toObject();
    const d = Math.floor(diff.days || 0);
    const h = Math.floor(diff.hours || 0);
    const m = Math.floor(diff.minutes || 0);

    let countdownText = '';
    if (d > 0) {
        // Shows "in 1 day 20 hr" or "in 2 days" (if hours is 0)
        countdownText = `in ${d} day${d !== 1 ? 's' : ''}${` ${h} hour(s)`}`;
    } else if (h > 0) {
        // Shows "in 5 hr 30 min"
        countdownText = `in ${h} hour(s)${` ${m} minutes`}`;
    } else {
        // Shows "in 15 min"
        countdownText = `in ${Math.max(1, m)} minutes`;
    }

    return (
        <div style={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            padding: '40px 20px', textAlign: 'center', background: '#11131A', 
            borderRadius: 16, border: '1px solid rgba(255,255,255,0.05)', 
            maxWidth: 800, margin: '0 auto', width: '100%' 
        }}>
            
            {isUpcoming && (
                <>
                    <div style={{ background: 'rgba(255,255,255,0.05)', color: '#8F9EB5', padding: '8px 24px', borderRadius: 20, fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20, width: '100%', maxWidth: '80%' }}>
                        Upcoming Match
                    </div>
                    <h2 style={{ fontSize: 26, color: '#FFF', margin: '0 0 10px 0' }}>{t1?.name || 'TBA'} vs {t2?.name || 'TBA'}</h2>
                    <div style={{ fontSize: 16, color: '#FFD700', fontWeight: 'bold', marginBottom: 5 }}>{countdownText}</div>
                    <div style={{ fontSize: 14, color: '#8F9EB5' }}>{matchStart.toFormat('EEEE, MMMM d @ h:mm a')}</div>
                </>
            )}
            
            {isLive && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(255, 51, 102, 0.15)', color: '#FF3366', padding: '8px 24px', borderRadius: 20, fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20, width: '100%', maxWidth: '80%' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF3366', boxShadow: '0 0 8px #FF3366' }}></div>
                        LIVE NOW
                    </div>
                    <h2 style={{ fontSize: 26, color: '#FFF', margin: '0 0 20px 0' }}>{t1?.name || 'TBA'} vs {t2?.name || 'TBA'}</h2>
                    {hasMedia ? (
                        <div style={{ width: '100%', background: '#0D0E15', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid #333' }}>
                            <MultiStreamPlayer event={detailedMatchRecord} />
                        </div>
                    ) : (
                        <div style={{ color: '#8F9EB5' }}>Broadcast starting soon...</div>
                    )}
                </>
            )}

            {isPast && (
                <>
                    <div style={{ background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', padding: '8px 24px', borderRadius: 20, fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20, width: '100%', maxWidth: '80%' }}>
                        Match Concluded
                    </div>
                    <h3 style={{ fontSize: 20, color: '#FFF', margin: '0 0 10px 0' }}>Waiting for Finalized Results</h3>
                    <p style={{ color: '#8F9EB5', fontSize: 14, maxWidth: 450, margin: '0 auto 20px auto', lineHeight: 1.5 }}>
                        This match has ended.  The board-by-board breakdown and past coverage will appear here shortly.
                    </p>
                    
                    {hasMedia && (
                        <div style={{ width: '100%', background: '#0D0E15', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid #333' }}>
                            <MultiStreamPlayer event={detailedMatchRecord} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default Standings;